import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, Filter, BrainCircuit, Hourglass } from 'lucide-react';

// A dedicated component for displaying formatted arguments for better separation of concerns.
const ArgumentsDisplay = ({ args, args_streaming }) => {
  // Return null if there are no arguments to display
  if ((!args || Object.keys(args).length === 0) && !args_streaming) {
    return (
      <div className="pl-11 pr-4 py-2 text-xs text-zinc-500 italic">
        No arguments provided.
      </div>
    );
  }

  // The arguments are inset to align with the action description, creating a clear visual hierarchy.
  return (
    <div className="pl-11 pr-4 pt-1 pb-3">
      {/* This inner container now handles the scrolling. */}
      <div className="bg-black/30 rounded-md p-3 max-h-[460px] overflow-y-auto custom-scrollbar">
        <pre className="text-xs text-zinc-400 whitespace-pre-wrap break-words font-mono">
          <code>
            {args && Object.keys(args).length > 0 ? (
              JSON.stringify(args, null, 2)
            ) : (
              args_streaming
            )}
          </code>
        </pre>
      </div>
    </div>
  );
};


const ActionTrackerEmbedded = ({ currentToolsStatus = [], toolCallApprovalList = [], isCurrent = false }) => {
  // State to track which action item is currently expanded
  const [expandedIndex, setExpandedIndex] = useState(null);


  // Pre-process actions to include parsed arguments 
  // Use memoization to avoid unnecessary recalculations
  const actions = React.useMemo(() => {
    // create a map from id to tool call approval status
    const approvalMap = new Map(toolCallApprovalList.map(approval => [approval.id, { approved: approval.approved, approvalNeeded: approval.approvalNeeded }]));
    return currentToolsStatus.map((toolCall) => {
      let argumentsParsed = {};
      let gotErrorWhileParsing = false;
      try {
        argumentsParsed = JSON.parse(toolCall.function?.arguments || "{}");
      } catch (e) {
        gotErrorWhileParsing = true;
      }

      const argsToDisplay = { ...argumentsParsed };
      delete argsToDisplay.action_description;

      return {
        id: toolCall.id,
        status: toolCall.status,
        running_phase: toolCall.running_phase,
        // if approval needed and not approved, set approvalNeeded to true
        approvalNeeded: approvalMap.get(toolCall.id) ? (approvalMap.get(toolCall.id).approvalNeeded && !approvalMap.get(toolCall.id).approved) : false,
        // action_description: argumentsParsed.action_description || "Call " + toolCall.function?.name,
        action_description: argumentsParsed.action_description || "Processing Action",
        tool_name: toolCall.function?.name,
        args: argsToDisplay,
        args_streaming: gotErrorWhileParsing ? toolCall.function?.arguments : null,
      };
    }).filter(action => action.tool_name !== "record_thought_process");
  }, [currentToolsStatus, toolCallApprovalList]); // Only recompute when currentToolsStatus changes


  const handleToggle = (index) => {
    // Temporarily disabled toggling to hide arguments during embedded use
    return;

    // If the clicked item is already open, close it. Otherwise, open the new one.
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  if (actions.length === 0) return null;

  return (
    <div className="w-full rounded-xl bg-zinc-900 border border-zinc-800 shadow-lg overflow-hidden mb-4">
      {/* Header */}
      {/* <div className="px-4 py-3 border-b border-zinc-800 flex items-center">
          {isCurrent ? (
            <div className="flex items-center space-x-2 mr-4">
              <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse"></div>
              <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
              <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
            </div>
          ) : null}
          <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-400">
            {isCurrent ? "Processing Tasks" : "Tasks"}
          </h3>
        </div> */}

      {/* Action Items */}
      <div className="divide-y divide-zinc-800/50">
        {actions.map((action, index) => {
          const isSuccess = action.status === 'success';
          const isFailed = action.status === 'failed';
          const isRunning = action.status === 'running' || (!isSuccess && !isFailed);
          const isExpanded = expandedIndex === index;

          // --- NEW: Logic to determine icon and color based on status and phase ---
          // --- MODIFIED: Logic to determine icon and color now includes approvalNeeded ---
          let statusIcon;
          let statusTextColor;

          if (isRunning) {
            // Check for the approval state first, as it takes priority
            if (action.approvalNeeded) {
              statusIcon = <Hourglass className="h-5 w-5 text-amber-400 animate-pulse" />;
              statusTextColor = 'text-amber-300';
            } else {
              // If not waiting for approval, proceed with phase checks
              const phase = action.running_phase || 'calling_tool';
              switch (phase) {
                case 'filtering':
                  statusIcon = <Filter className="h-5 w-5 text-indigo-300 animate-pulse" />;
                  statusTextColor = 'text-cyan-300';
                  break;
                case 'analyzing':
                  statusIcon = <BrainCircuit className="h-5 w-5 text-indigo-400 animate-analyzing-glow" />;
                  statusTextColor = 'text-indigo-300';
                  break;
                case 'calling_tool':
                default:
                  // statusIcon = <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />;
                  statusIcon = <img src="../assets/logo.png" className="w-6 animate-rotatePause" />;
                  statusTextColor = 'text-blue-300';
                  break;
              }
            }
          } else if (isSuccess) {
            statusIcon = <CheckCircle className="h-5 w-5 text-emerald-400" />;
            statusTextColor = 'text-zinc-300';
          } else if (isFailed) {
            statusIcon = <XCircle className="h-5 w-5 text-red-400" />;
            statusTextColor = 'text-red-300';
          }


          return (
            // This container now handles the background change and click event for a unified feel.
            <div
              key={index}
              data-tool-id={action.id}
              className={`transition-colors duration-300 cursor-pointer ${isExpanded ? 'bg-zinc-800/40' : 'hover:bg-zinc-800/40'}`}
              onClick={() => handleToggle(index)}
            >
              {/* Clickable area for toggling - now a relative container for the progress bar */}
              <div
                className={`relative px-4 py-2 flex items-start sm:items-center gap-3 transition-colors duration-200`}
              >
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-1 mb-1">
                  {/* --- MODIFIED: Use the determined status icon --- */}
                  {statusIcon}
                </div>

                {/* Main content area */}
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-y-2 gap-x-4">
                  <div className="flex-1 min-w-0">
                    {/* <p className={`text-xs font-medium truncate ${isFailed ? 'text-red-300' : isSuccess ? 'text-zinc-300' : 'text-blue-300'}`}> */}
                    <p className={`text-xs font-medium truncate ${statusTextColor}`}>
                      {action.action_description}
                    </p>
                  </div>
                  {/* This wrapper ensures the badge is pushed to the right on mobile */}
                  {/* <div className="w-full sm:w-auto flex justify-end flex-shrink-0">
                    <span className="inline-block bg-zinc-800 text-zinc-400 text-xs font-mono px-2 py-1 rounded-md">
                      {action.tool_name}
                    </span>
                  </div> */}
                </div>

                {/* The progress bar is now absolutely positioned relative to the whole row */}
                {(isRunning && !action.approvalNeeded) && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-700/50 overflow-hidden">
                    <div
                      className="h-full bg-primary_c origin-left"
                      style={{ animation: 'fillProgress 30s ease-out forwards' }}
                    ></div>
                  </div>
                )}
              </div>

              {/* Expandable Arguments Section */}
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ maxHeight: isExpanded ? '500px' : '0' }}
              >
                <ArgumentsDisplay args={action.args} args_streaming={action.args_streaming} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      {/* <div className="px-4 py-2 bg-zinc-800/50 text-xs text-zinc-500 flex justify-between">
          <span>{actions.filter(a => a.status === 'success').length} completed</span>
          {isCurrent
            ? <span>{actions.filter(a => a.status === 'running').length} in progress</span>
            : <span>{actions.filter(a => a.status === 'failed').length} failed</span>
          }
        </div> */}


      {/* --- MODIFIED ANIMATION --- */}
      {/* Global styles for animations and custom scrollbar */}
      <style jsx global>{`
          /* Reverted to the original animation concept using a modern, performant transform */
          @keyframes fillProgress {
            0% { transform: scaleX(0); }
            10% { transform: scaleX(0.4); }
            20% { transform: scaleX(0.6); }
            30% { transform: scaleX(0.7); }
            40% { transform: scaleX(0.75); }
            50% { transform: scaleX(0.8); }
            60% { transform: scaleX(0.83); }
            70% { transform: scaleX(0.86); }
            80% { transform: scaleX(0.88); }
            90% { transform: scaleX(0.90); }
            95% { transform: scaleX(0.91); }
            97% { transform: scaleX(0.92); }
            99% { transform: scaleX(0.93); }
            100% { transform: scaleX(0.94); }
          }
          
          /* Custom Scrollbar for Webkit browsers */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: #3f3f46; /* zinc-700 */
            border-radius: 10px;
            border: 2px solid transparent;
            background-clip: content-box;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: #52525b; /* zinc-600 */
          }
          
          /* Custom Scrollbar for Firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #3f3f46 transparent; /* thumb and track */
          }
        `}</style>
    </div>
  );
};

export default ActionTrackerEmbedded;
